import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  panels: { key: string; node: ReactNode }[];
  index: number;
  onIndex: (i: number) => void;
}

/**
 * Horizontal pager: map, then the panels behind it.
 *
 * Drag distance is capped at the container width and rubber-banded at the ends,
 * so a hard swipe at panel 0 does not tear the map off the screen.
 *
 * Deliberately supports three input methods, because this runs on three very
 * different surfaces: touch drag on a phone or head unit, trackpad two-finger
 * swipe on the laptop it gets demoed from, and arrow keys.
 */
export default function SwipePager({ panels, index, onIndex }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<'x' | 'y' | null>(null);
  const wheelAcc = useRef(0);
  const wheelLock = useRef(false);

  const width = () => el.current?.clientWidth ?? 1;
  const clampIndex = useCallback(
    (i: number) => Math.max(0, Math.min(panels.length - 1, i)),
    [panels.length]
  );

  // --- touch ---------------------------------------------------------------
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
    axis.current = null;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!start.current) return;
    const t = e.touches[0];
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;

    // Lock the axis on first meaningful movement. Without this, panning the map
    // vertically drags the pager sideways and vice versa.
    if (!axis.current) {
      if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return;
      axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (axis.current !== 'x') return;

    const atStart = index === 0 && dx > 0;
    const atEnd = index === panels.length - 1 && dx < 0;
    setDrag(atStart || atEnd ? dx * 0.25 : dx); // rubber band at the ends
  };

  const onTouchEnd = () => {
    if (axis.current === 'x') {
      const threshold = Math.min(90, width() * 0.18);
      if (drag < -threshold) onIndex(clampIndex(index + 1));
      else if (drag > threshold) onIndex(clampIndex(index - 1));
    }
    start.current = null;
    axis.current = null;
    setDrag(0);
  };

  // --- trackpad ------------------------------------------------------------
  useEffect(() => {
    const node = el.current;
    if (!node) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      if (wheelLock.current) return;
      wheelAcc.current += e.deltaX;
      if (Math.abs(wheelAcc.current) > 120) {
        onIndex(clampIndex(index + (wheelAcc.current > 0 ? 1 : -1)));
        wheelAcc.current = 0;
        // A trackpad flick emits a long tail of events; ignore it.
        wheelLock.current = true;
        window.setTimeout(() => {
          wheelLock.current = false;
        }, 450);
      }
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [index, clampIndex, onIndex]);

  // --- keyboard ------------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /input|textarea|select/i.test(target.tagName)) return;
      if (e.key === 'ArrowRight') onIndex(clampIndex(index + 1));
      if (e.key === 'ArrowLeft') onIndex(clampIndex(index - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, clampIndex, onIndex]);

  const offset = -index * 100;

  return (
    <div
      className="pager"
      ref={el}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="pager-track"
        style={{
          transform: `translateX(calc(${offset}% + ${drag}px))`,
          transition: drag ? 'none' : 'transform 280ms cubic-bezier(.22,.61,.36,1)',
        }}
      >
        {panels.map((p) => (
          <section className="pager-panel" key={p.key}>
            {p.node}
          </section>
        ))}
      </div>

      {panels.length > 1 && (
        <div className="pager-dots">
          {panels.map((p, i) => (
            <button
              key={p.key}
              className={`dot ${i === index ? 'on' : ''}`}
              onClick={() => onIndex(i)}
              aria-label={`Panel ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
